import React from 'react';
import {
  Box,
  NumberInput,
  Select,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { opts, titleOptions } from 'src/constants/nomineeForm';
import { UseFormReturnType } from '@mantine/form';
import { isEighteenYearsOld } from '../../utils/helpers';
import { useStyles } from './nomineeDetails.styles';

type NomineeDetailsFormProps = {
  form: UseFormReturnType<{
    users: {
      title: string;
      firstName: string;
      lastName: string;
      relationship: string;
      dateOfBirth: string;
      guardianName: string;
      guardianAddress: string;
      guardianEmail: string;
      guardianPhone: string;
      guardianRelation: string;
      phone: string;
      email: string;
      addressLineOne: string;
      addressLineTwo: string;
      pincode: string;
      percentage: string;
      id: string;
    }[]
  }>,
}

const NomineeDetailsForm = ({ form } : NomineeDetailsFormProps) => {
  const { classes, cx } = useStyles();

  const handleRemove = (index: number) => () => form.setFieldValue(
    "users",
    form.values.users.filter((_, i) => i !== index)
  );

  return (
    <form className={classes.formWrapper}>
      {form.values.users.map((user, index) => (
        <React.Fragment key={user.id}>
          <Box className={classes.formHeader} mt={index > 0 ? 24 : 0}>
            <p className={classes.description}>{`Nominee ${index + 1}`}</p>
            {index > 0 ? (
              <button
                type="button"
                className={cx(classes.changeNumberLink, classes.removeBtn)}
                onClick={handleRemove(index)}
              >
                Remove
              </button>
            ) : null}
          </Box>

          <Select
            placeholder="Title"
            data={titleOptions}
            {...form.getInputProps(`users.${index}.title`)}
          />

          <TextInput
            placeholder="First name"
            maxLength={30}
            {...form.getInputProps(`users.${index}.firstName`)}
            type="text"
          />

          <TextInput
            placeholder="Last name"
            maxLength={30}
            {...form.getInputProps(`users.${index}.lastName`)}
          />

          <Select
            placeholder="Relationship to the client"
            data={opts}
            {...form.getInputProps(`users.${index}.relationship`)}
          />

          <DateInput
            placeholder="Date of birth"
            maxDate={new Date()}
            minDate={new Date("01/01/1900")}
            {...form.getInputProps(`users.${index}.dateOfBirth`)}
            valueFormat="DD/MM/YYYY"
            popoverProps={{ position: "top-start" }}
          />

          <NumberInput
            placeholder="Mobile number"
            {...form.getInputProps(`users.${index}.phone`)}
            maxLength={10}
            min={0}
            hideControls
          />

          <TextInput
            maxLength={50}
            placeholder="Email address"
            {...form.getInputProps(`users.${index}.email`)}
          />

          <TextInput
            maxLength={60}
            placeholder="Address line 1"
            {...form.getInputProps(`users.${index}.addressLineOne`)}
          />

          <TextInput
            maxLength={60}
            placeholder="Address line 2 (optional)"
            {...form.getInputProps(`users.${index}.addressLineTwo`)}
          />

          <NumberInput
            placeholder="Pincode"
            {...form.getInputProps(`users.${index}.pincode`)}
            maxLength={6}
            minLength={6}
            hideControls
          />

          <NumberInput
            min={1}
            max={100}
            precision={2}
            placeholder="Share percentage"
            {...form.getInputProps(`users.${index}.percentage`)}
            type="number"
            rightSection="%"
            maxLength={3}
          />

          {user.dateOfBirth && !isEighteenYearsOld(user.dateOfBirth) && (
          <>
            <TextInput
              placeholder="Guardian Name"
              maxLength={30}
              {...form.getInputProps(`users.${index}.guardianName`)}
            />

            <NumberInput
              placeholder="Guardian Phone"
              {...form.getInputProps(`users.${index}.guardianPhone`)}
              maxLength={10}
              min={0}
              hideControls
            />

            <TextInput
              maxLength={50}
              placeholder="Guardian Email"
              {...form.getInputProps(`users.${index}.guardianEmail`)}
            />

            <Select
              placeholder="Relationship to the Guardian"
              data={opts}
              {...form.getInputProps(`users.${index}.guardianRelation`)}
            />

            <TextInput
              maxLength={60}
              placeholder="Guardian Address"
              {...form.getInputProps(`users.${index}.guardianAddress`)}
            />
          </>
          )}
        </React.Fragment>

      ))}
    </form>
  )
}

export default NomineeDetailsForm
